import clsx from 'clsx'
import { useCallback, useEffect, useState } from 'react'
import { Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import 'swiper/css/a11y'
import { A11y } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { SpecialPromotionItem, SpecialPromotionProduct } from '@/entities/cart/api/special-promotion'

import { useMediaQueries } from '@/shared/lib/hooks/useMediaQueries'
import { FullScreenModal } from '@/shared/ui/full-screen-modal'
import { Modal } from '@/shared/ui/Modal'
import { SliderNextButton, SliderPrevButton } from '@/shared/ui/SliderButtons'

import { PromotionProductCard } from '../promotion-product-card/promotion-product-card'

import cl from './promotion-products-modal.module.scss'

type PromotionProductsModalProps = {
	items: SpecialPromotionItem[]
	isOpen: boolean
	loadingProductId?: number
	onClose: () => void
	onBuy: (product: SpecialPromotionProduct) => void
}

type PromotionProductsGroupProps = {
	products: SpecialPromotionProduct[]
	loadingProductId?: number
	onBuy: (product: SpecialPromotionProduct) => void
}

const BREAKPOINTS = {
	0: {
		slidesPerView: 1,
		spaceBetween: 8
	},
	577: {
		slidesPerView: 2,
		spaceBetween: 8
	},
	1024: {
		slidesPerView: 3,
		spaceBetween: 8
	}
}

const getSlidesPerView = (swiper: SwiperType | null) => {
	if (!swiper) return 1

	const slidesPerView = swiper.params.slidesPerView

	return typeof slidesPerView === 'number' ? slidesPerView : 1
}

const PromotionProductsGroup = (props: PromotionProductsGroupProps) => {
	const { products, loadingProductId, onBuy } = props
	const [activeIndex, setActiveIndex] = useState(0)
	const [slidesPerView, setSlidesPerView] = useState(1)
	const [isBeginning, setIsBeginning] = useState(true)
	const [isEnd, setIsEnd] = useState(false)

	const updateSliderState = useCallback((swiperInstance: SwiperType) => {
		setActiveIndex(swiperInstance.activeIndex)
		setSlidesPerView(getSlidesPerView(swiperInstance))
		setIsBeginning(swiperInstance.isBeginning)
		setIsEnd(swiperInstance.isEnd)
	}, [])

	const showNavigation = slidesPerView > 1 && products.length > slidesPerView

	return (
		<section>
			<div className={cl.slider}>
				<div className={cl.viewport}>
					<Swiper
						className={cl.swiper}
						modules={[A11y]}
						breakpoints={BREAKPOINTS}
						watchOverflow={true}
						observer={true}
						observeParents={true}
						onSwiper={updateSliderState}
						onSlideChange={updateSliderState}
						onBreakpoint={updateSliderState}
						onResize={updateSliderState}>
						{products.map((product, index) => (
							<SwiperSlide key={`${product.ID}-${index}`} className={cl.slide}>
								<PromotionProductCard product={product} isLoading={loadingProductId === product.ID} onBuy={onBuy} />
							</SwiperSlide>
						))}
						{showNavigation && (
							<div className={cl.buttons}>
								<SliderPrevButton className={cl.buttons__prevButton} disabled={isBeginning} />
								<SliderNextButton className={cl.buttons__nextButton} disabled={isEnd} />
							</div>
						)}
					</Swiper>
				</div>
			</div>
			{products.length > 1 && (
				<div className={cl.pagination} aria-hidden="true">
					{products.map((product, index) => (
						<span key={product.ID} className={activeIndex === index ? cl.paginationActive : undefined} />
					))}
				</div>
			)}
		</section>
	)
}

const PromotionProductsModalContent = (props: PromotionProductsModalProps) => {
	const { items, loadingProductId, onBuy } = props
	const [activeItemIndex, setActiveItemIndex] = useState(0)
	const activeItem = items[activeItemIndex] ?? items[0]
	const activeItemText = activeItem ? activeItem.text || `Добавьте со скидкой ${activeItem.discountLabel}` : ''

	useEffect(() => {
		setActiveItemIndex(0)
	}, [items])

	if (!activeItem) return null

	return (
		<div className={cl.promotion}>
			<h2 className={cl.heading}>Выгода при покупке комплектом</h2>
			<p className={clsx(cl.description, items.length > 1 && cl.descriptionWithTabs)}>{activeItemText}</p>
			{items.length > 1 && (
				<div className={cl.tabs} role="tablist" aria-label="Скидка на второй товар">
					{items.map((item, index) => (
						<button
							key={item.discountLabel}
							type="button"
							role="tab"
							aria-selected={activeItemIndex === index}
							className={clsx(cl.tabButton, activeItemIndex === index && cl.tabButtonActive)}
							onClick={() => setActiveItemIndex(index)}>
							{item.discountLabel}
						</button>
					))}
				</div>
			)}
			{activeItem && (
				<PromotionProductsGroup
					key={activeItem.discountLabel}
					products={activeItem.products}
					loadingProductId={loadingProductId}
					onBuy={onBuy}
				/>
			)}
		</div>
	)
}

export const PromotionProductsModal = (props: PromotionProductsModalProps) => {
	const { isOpen, onClose } = props
	const { isMobile } = useMediaQueries()

	if (!isOpen) return null

	if (isMobile) {
		return (
			<FullScreenModal open={isOpen} onClose={onClose}>
				<PromotionProductsModalContent {...props} />
			</FullScreenModal>
		)
	}

	return (
		<Modal isOpen={isOpen} onClose={onClose} className={cl.modal} contentClassName={cl.content}>
			<PromotionProductsModalContent {...props} />
		</Modal>
	)
}
